import { ref } from 'vue'
import type { Ref } from 'vue'
import type { Event, StoreEventPayload, UpdateEventPayload } from '@/types/events'
import {
  listEvents as apiListEvents,
  createEvent as apiCreateEvent,
  getEvent as apiGetEvent,
  updateEvent as apiUpdateEvent,
  deleteEvent as apiDeleteEvent,
} from '@/api/events'
import { useAuth } from '@/composables/useAuth'

const { currentOrganisation } = useAuth()

const events = ref<Event[]>([])
const event = ref<Event | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const lastSavedAt = ref<Date | null>(null)

async function fetchEvents(): Promise<void> {
  if (!currentOrganisation.value) return
  loading.value = true
  error.value = null
  try {
    events.value = await apiListEvents(currentOrganisation.value.id)
  } catch {
    error.value = 'events.list.error_load'
  } finally {
    loading.value = false
  }
}

async function fetchEvent(eventSlug: string): Promise<void> {
  loading.value = true
  error.value = null
  try {
    event.value = await apiGetEvent(eventSlug)
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function createEvent(payload: StoreEventPayload): Promise<Event> {
  if (!currentOrganisation.value) throw new Error('No organisation selected')
  const created = await apiCreateEvent(currentOrganisation.value.id, payload)
  events.value = [...events.value, created]
  return created
}

async function updateEvent(eventSlug: string, payload: UpdateEventPayload): Promise<Event> {
  const updated = await apiUpdateEvent(eventSlug, payload)
  event.value = updated
  events.value = events.value.map((e) => (e.slug === eventSlug ? updated : e))
  lastSavedAt.value = new Date()
  return updated
}

async function deleteEvent(eventSlug: string): Promise<void> {
  await apiDeleteEvent(eventSlug)
  events.value = events.value.filter((e) => e.slug !== eventSlug)
}

export function useEvents(): {
  events: Ref<Event[]>
  event: Ref<Event | null>
  loading: Ref<boolean>
  error: Ref<string | null>
  lastSavedAt: Ref<Date | null>
  fetchEvents: () => Promise<void>
  fetchEvent: (eventSlug: string) => Promise<void>
  createEvent: (payload: StoreEventPayload) => Promise<Event>
  updateEvent: (eventSlug: string, payload: UpdateEventPayload) => Promise<Event>
  deleteEvent: (eventSlug: string) => Promise<void>
} {
  return {
    events,
    event,
    loading,
    error,
    lastSavedAt,
    fetchEvents,
    fetchEvent,
    createEvent,
    updateEvent,
    deleteEvent,
  }
}
